import { AdornmentType } from 'app/shared/componentsRestyled/coreDesignSystem/form/DataFieldCDS/DataFieldCDS.constants';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { baseStatusOptions } from 'constants/options';
import { getConditionalArray, getConditionalObject } from 'utils/data';

import {
  DISCOUNT_PREMIUM_AMOUNT_FIELD,
  DISCOUNT_PREMIUM_AMOUNT_DECIMAL_SCALE,
  DISCOUNT_PREMIUM_PERCENT_DECIMAL_SCALE,
  DISCOUNT_PREMIUM_PERCENT_FIELD,
  DiscountPremiumFieldWidthPercent,
  POST_CLOSING_LONG_TEXT_MAX_LENGTH,
  POST_CLOSING_SHORT_TEXT_MAX_LENGTH,
  SELECT_PLACEHOLDER,
  servicingStatusOptions,
} from '../PostClosing.constants';

import { SECTION_HREF_MAP } from './EditDrawer.hrefs';
import {
  doubleWidthFieldWrapperStyles,
  fixedTrackGroupStyles,
  fullRowFieldWrapperStyles,
} from './EditDrawer.styles';
import { EditDrawerTestIds } from './EditDrawer.testIds';

export { SECTION_HREF_MAP } from './EditDrawer.hrefs';

export const UNSECURED_HIDDEN_SECTIONS = new Set([
  SECTION_HREF_MAP.SHIPPING,
  SECTION_HREF_MAP.MERS,
]);

const getDoubleWidthProps = isDoubleWidth =>
  getConditionalObject(isDoubleWidth, { wrapperStyles: doubleWidthFieldWrapperStyles });

const getDateField = ({ fieldId, label, dataTestId, isDoubleWidth = false }) => ({
  fieldId,
  type: FieldType.DATE,
  nameInModel: fieldId,
  inputProps: {
    label,
    variant: 'outlined',
    format: COMMON_DATE_FORMAT,
    dataTestId,
    widthInColumns: 2,
    ...getDoubleWidthProps(isDoubleWidth),
  },
});

const getLongTextField = ({ fieldId, label, dataTestId }) => ({
  fieldId,
  type: FieldType.INPUT,
  nameInModel: fieldId,
  inputProps: {
    type: FieldInputType.TEXT,
    label,
    variant: 'outlined',
    multiline: true,
    rows: 3,
    maxLength: POST_CLOSING_LONG_TEXT_MAX_LENGTH,
    dataTestId,
    widthInColumns: 4,
    wrapperStyles: fullRowFieldWrapperStyles,
  },
});

const getShortTextField = ({ fieldId, label, dataTestId, isDoubleWidth = false }) => ({
  fieldId,
  type: FieldType.INPUT,
  nameInModel: fieldId,
  inputProps: {
    type: FieldInputType.TEXT,
    label,
    variant: 'outlined',
    maxLength: POST_CLOSING_SHORT_TEXT_MAX_LENGTH,
    dataTestId,
    widthInColumns: 2,
    ...getDoubleWidthProps(isDoubleWidth),
  },
});

const getCheckboxField = ({ fieldId, label, dataTestId, isDoubleWidth = false }) => ({
  fieldId,
  type: FieldType.CHECKBOX,
  nameInModel: fieldId,
  dataTestId,
  inputProps: {
    label,
    widthInColumns: 2,
    ...getDoubleWidthProps(isDoubleWidth),
  },
});

const getSelectField = ({ fieldId, label, options, dataTestId, isDoubleWidth = false }) => ({
  fieldId,
  type: FieldType.SELECT,
  nameInModel: fieldId,
  inputProps: {
    label,
    variant: 'outlined',
    placeholder: SELECT_PLACEHOLDER,
    options,
    withSortedOptions: false,
    dataTestId,
    widthInColumns: 2,
    ...getDoubleWidthProps(isDoubleWidth),
  },
});

const servicingOwnershipTypeOptions = [
  { value: 1, name: 'Wholly Owned' },
  { value: 2, name: 'MSR Only' },
];

const ServicingInvestorType = {
  FANNIE_MAE: 1,
  FREDDIE_MAC: 2,
  FHA: 3,
  VA: 4,
  USDA: 5,
  PRIVATE: 6,
  OTHER: 7,
};

const servicingInvestorTypeOptions = [
  { value: ServicingInvestorType.FANNIE_MAE, name: 'Fannie Mae' },
  { value: ServicingInvestorType.FREDDIE_MAC, name: 'Freddie Mac' },
  { value: ServicingInvestorType.FHA, name: 'FHA' },
  { value: ServicingInvestorType.VA, name: 'VA' },
  { value: ServicingInvestorType.USDA, name: 'USDA' },
  { value: ServicingInvestorType.PRIVATE, name: 'Private' },
  { value: ServicingInvestorType.OTHER, name: 'Other' },
];

const unsecuredInvestorTypeValues = new Set([
  ServicingInvestorType.PRIVATE,
  ServicingInvestorType.OTHER,
]);

const getServicingInvestorTypeOptions = isUnsecured =>
  isUnsecured
    ? servicingInvestorTypeOptions.filter(({ value }) => unsecuredInvestorTypeValues.has(value))
    : servicingInvestorTypeOptions;

export const getInvestorFormConfig = ({ investorsOptions, isUnsecured = false }) => ({
  groups: [
    {
      type: FieldType.GROUP,
      key: 'investor-info',
      title: 'Investor Information',
      fields: [
        {
          fieldId: 'investorId',
          type: FieldType.SELECT,
          nameInModel: 'investorId',
          inputProps: {
            label: 'Investor',
            variant: 'outlined',
            options: investorsOptions,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'investorLoanNumber',
          type: FieldType.INPUT,
          nameInModel: 'investorLoanNumber',
          inputProps: {
            type: FieldInputType.TEXT,
            label: 'Investor Loan Number',
            variant: 'outlined',
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'dueDate',
          type: FieldType.DATE,
          nameInModel: 'dueDate',
          inputProps: {
            label: 'Due Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'investorPackageUploadedDate',
          type: FieldType.DATE,
          nameInModel: 'investorPackageUploadedDate',
          inputProps: {
            label: 'Package Uploaded Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        ...getConditionalArray(!isUnsecured, [
          {
            fieldId: 'commitmentNumber',
            type: FieldType.INPUT,
            nameInModel: 'commitmentNumber',
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Commitment Number',
              variant: 'outlined',
              widthInColumns: 2,
            },
          },
        ]),
      ],
    },
  ],
});

export const getAccountingFormConfig = ({ isUnsecured = false } = {}) => ({
  groups: [
    {
      type: FieldType.GROUP,
      key: 'accounting-info',
      title: 'Accounting Information',
      fields: [
        {
          fieldId: 'principalBalance',
          type: FieldType.INPUT,
          nameInModel: 'principalBalance',
          noValidate: true,
          inputProps: {
            type: FieldInputType.CURRENCY,
            label: 'Principal Balance',
            variant: 'outlined',
            widthInColumns: 2,
            disabled: true,
          },
        },
        {
          fieldId: 'principalPurchased',
          type: FieldType.INPUT,
          nameInModel: 'principalPurchased',
          noValidate: true,
          inputProps: {
            type: FieldInputType.CURRENCY,
            label: 'Principal Purchased',
            variant: 'outlined',
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'discountPremium',
          type: FieldType.COMBINED,
          nameInModel: DISCOUNT_PREMIUM_AMOUNT_FIELD,
          combinedFields: [
            {
              nameInModel: DISCOUNT_PREMIUM_AMOUNT_FIELD,
              noValidate: true,
              widthPercent: DiscountPremiumFieldWidthPercent.AMOUNT,
              shouldShowRawWhileFocused: true,
              inputProps: {
                type: FieldInputType.CURRENCY,
                adornmentType: AdornmentType.START,
                required: false,
                decimalScale: DISCOUNT_PREMIUM_AMOUNT_DECIMAL_SCALE,
                fixedDecimalScale: true,
                dataTestId: EditDrawerTestIds.ACCOUNTING_FORM_DISCOUNT_PREMIUM_AMOUNT_INPUT,
              },
            },
            {
              nameInModel: DISCOUNT_PREMIUM_PERCENT_FIELD,
              noValidate: true,
              widthPercent: DiscountPremiumFieldWidthPercent.PERCENT,
              shouldShowRawWhileFocused: true,
              inputProps: {
                type: FieldInputType.PERCENTAGE_RESTYLED,
                adornmentType: AdornmentType.END,
                required: false,
                decimalScale: DISCOUNT_PREMIUM_PERCENT_DECIMAL_SCALE,
                decimalSeparator: '.',
                fixedDecimalScale: true,
                isNumericString: true,
                dataTestId: EditDrawerTestIds.ACCOUNTING_FORM_DISCOUNT_PREMIUM_PERCENT_INPUT,
              },
            },
          ],
          inputProps: {
            label: 'Discount/Premium',
            required: false,
            variant: 'outlined',
            widthInColumns: 2,
            wrapperStyles: doubleWidthFieldWrapperStyles,
          },
        },
        ...getConditionalArray(!isUnsecured, [
          {
            fieldId: 'fundingFee',
            type: FieldType.INPUT,
            nameInModel: 'fundingFee',
            noValidate: true,
            inputProps: {
              type: FieldInputType.CURRENCY,
              label: 'Funding Fee',
              variant: 'outlined',
              widthInColumns: 2,
            },
          },
          {
            fieldId: 'taxFee',
            type: FieldType.INPUT,
            nameInModel: 'taxFee',
            noValidate: true,
            inputProps: {
              type: FieldInputType.CURRENCY,
              label: 'Tax Fee',
              variant: 'outlined',
              widthInColumns: 2,
            },
          },
          {
            fieldId: 'escrowBalance',
            type: FieldType.INPUT,
            nameInModel: 'escrowBalance',
            noValidate: true,
            inputProps: {
              type: FieldInputType.CURRENCY,
              label: 'Escrow Balance',
              variant: 'outlined',
              widthInColumns: 2,
            },
          },
        ]),
        {
          fieldId: 'interest',
          type: FieldType.INPUT,
          nameInModel: 'interest',
          noValidate: true,
          inputProps: {
            type: FieldInputType.CURRENCY,
            label: 'Interest',
            variant: 'outlined',
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'investorWireAmount',
          type: FieldType.INPUT,
          nameInModel: 'investorWireAmount',
          noValidate: true,
          inputProps: {
            type: FieldInputType.CURRENCY,
            label: 'Investor Wire Amount',
            variant: 'outlined',
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'firstPayment',
          type: FieldType.DATE,
          nameInModel: 'firstPayment',
          inputProps: {
            label: 'First Payment',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'firstDueToInvestor',
          type: FieldType.DATE,
          nameInModel: 'firstDueToInvestor',
          inputProps: {
            label: 'First Due To Investor',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
      ],
    },
  ],
});

export const getServicerFormConfig = ({ servicingCompaniesOptions, isUnsecured = false }) => ({
  groups: [
    {
      type: FieldType.GROUP,
      key: 'servicer-info',
      title: 'Servicer Information',
      fields: [
        {
          fieldId: 'servicer',
          type: FieldType.SELECT,
          nameInModel: 'servicer',
          inputProps: {
            label: 'Servicer',
            variant: 'outlined',
            options: servicingCompaniesOptions,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'servicerLoanNumber',
          type: FieldType.INPUT,
          nameInModel: 'servicerLoanNumber',
          inputProps: {
            type: FieldInputType.TEXT,
            label: 'Servicer Loan Number',
            variant: 'outlined',
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'transmitDate',
          type: FieldType.DATE,
          nameInModel: 'transmitDate',
          inputProps: {
            label: 'Servicer Transmit Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        ...getConditionalArray(!isUnsecured, [
          {
            fieldId: 'min',
            type: FieldType.INPUT,
            nameInModel: 'min',
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'MIN / MERS Number',
              variant: 'outlined',
              widthInColumns: 2,
            },
          },
        ]),
        {
          fieldId: 'servicingOwnershipType',
          type: FieldType.SELECT,
          nameInModel: 'servicingOwnershipType',
          inputProps: {
            label: 'Servicing Ownership Type',
            variant: 'outlined',
            options: servicingOwnershipTypeOptions,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'servicingStatus',
          type: FieldType.SELECT,
          nameInModel: 'servicingStatus',
          inputProps: {
            label: 'Servicing Status',
            variant: 'outlined',
            options: servicingStatusOptions,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'servicingStartDate',
          type: FieldType.DATE,
          nameInModel: 'servicingStartDate',
          inputProps: {
            label: 'Servicing Start Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'servicingEndDate',
          type: FieldType.DATE,
          nameInModel: 'servicingEndDate',
          inputProps: {
            label: 'Servicing End Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        ...getConditionalArray(!isUnsecured, [
          {
            fieldId: 'poolNumber',
            type: FieldType.INPUT,
            nameInModel: 'poolNumber',
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Pool Number',
              variant: 'outlined',
              widthInColumns: 2,
            },
          },
        ]),
        {
          fieldId: 'investorType',
          type: FieldType.SELECT,
          nameInModel: 'investorType',
          inputProps: {
            label: 'Investor Type',
            variant: 'outlined',
            options: getServicingInvestorTypeOptions(isUnsecured),
            widthInColumns: 2,
          },
        },
      ],
    },
  ],
});

const shippingGroupStyles = { gridTemplateColumns: '1fr 1fr' };

export const SHIPPING_FORM_CONFIG = {
  groups: [
    {
      type: FieldType.GROUP,
      key: 'shipping-title-company-to-lender',
      title: 'Title Company to Lender',
      customStyles: shippingGroupStyles,
      fields: [
        {
          fieldId: 'trackingNumberTitleCompanyToLender',
          type: FieldType.INPUT,
          nameInModel: 'trackingNumberTitleCompanyToLender',
          inputProps: {
            type: FieldInputType.TEXT,
            label: 'Tracking # Title Company to Lender',
            variant: 'outlined',
            widthInColumns: 1,
          },
        },
        {
          fieldId: 'shippedTitleCompanyToLenderDate',
          type: FieldType.DATE,
          nameInModel: 'shippedTitleCompanyToLenderDate',
          inputProps: {
            label: 'Shipped Title Company to Lender Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 1,
          },
        },
        {
          fieldId: 'receivedTitleCompanyToLenderDate',
          type: FieldType.DATE,
          nameInModel: 'receivedTitleCompanyToLenderDate',
          inputProps: {
            label: 'Received Title Company to Lender Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 1,
          },
        },
      ],
    },
    {
      type: FieldType.GROUP,
      key: 'shipping-collateral-to-warehouse',
      title: 'Collateral to Warehouse',
      customStyles: shippingGroupStyles,
      fields: [
        {
          fieldId: 'collateralTrackingNumberToWarehouseBank',
          type: FieldType.INPUT,
          nameInModel: 'collateralTrackingNumberToWarehouseBank',
          inputProps: {
            type: FieldInputType.TEXT,
            label: 'Collateral Tracking # to Warehouse',
            variant: 'outlined',
            widthInColumns: 1,
          },
        },
        {
          fieldId: 'collateralShippedToWarehouseDate',
          type: FieldType.DATE,
          nameInModel: 'collateralShippedToWarehouseDate',
          inputProps: {
            label: 'Collateral Shipped to Warehouse Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 1,
          },
        },
        {
          fieldId: 'collateralReceivedToWarehouseDate',
          type: FieldType.DATE,
          nameInModel: 'collateralReceivedToWarehouseDate',
          inputProps: {
            label: 'Collateral Received to Warehouse Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 1,
          },
        },
      ],
    },
    {
      type: FieldType.GROUP,
      key: 'shipping-note-to-warehouse',
      title: 'Note to Warehouse',
      customStyles: shippingGroupStyles,
      fields: [
        {
          fieldId: 'noteTrackingNumberToWarehouse',
          type: FieldType.INPUT,
          nameInModel: 'noteTrackingNumberToWarehouse',
          inputProps: {
            type: FieldInputType.TEXT,
            label: 'Note Tracking # to Warehouse',
            variant: 'outlined',
            widthInColumns: 1,
          },
        },
        {
          fieldId: 'noteShippedToWarehouseDate',
          type: FieldType.DATE,
          nameInModel: 'noteShippedToWarehouseDate',
          inputProps: {
            label: 'Note Shipped to Warehouse Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 1,
          },
        },
        {
          fieldId: 'noteReceivedToWarehouseDate',
          type: FieldType.DATE,
          nameInModel: 'noteReceivedToWarehouseDate',
          inputProps: {
            label: 'Note Received to Warehouse Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 1,
          },
        },
      ],
    },
    {
      type: FieldType.GROUP,
      key: 'shipping-collateral-to-investor',
      title: 'Collateral to Investor',
      customStyles: shippingGroupStyles,
      fields: [
        {
          fieldId: 'collateralTrackingNumberToInvestor',
          type: FieldType.INPUT,
          nameInModel: 'collateralTrackingNumberToInvestor',
          inputProps: {
            type: FieldInputType.TEXT,
            label: 'Collateral Tracking # to Investor',
            variant: 'outlined',
            widthInColumns: 1,
          },
        },
        {
          fieldId: 'collateralShippedToInvestorDate',
          type: FieldType.DATE,
          nameInModel: 'collateralShippedToInvestorDate',
          inputProps: {
            label: 'Collateral Shipped to Investor Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 1,
          },
        },
        {
          fieldId: 'collateralReceivedToInvestorDate',
          type: FieldType.DATE,
          nameInModel: 'collateralReceivedToInvestorDate',
          inputProps: {
            label: 'Collateral Received to Investor Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 1,
          },
        },
      ],
    },
    {
      type: FieldType.GROUP,
      key: 'shipping-note-to-investor',
      title: 'Note to Investor',
      customStyles: shippingGroupStyles,
      fields: [
        {
          fieldId: 'noteTrackingNumberToInvestor',
          type: FieldType.INPUT,
          nameInModel: 'noteTrackingNumberToInvestor',
          inputProps: {
            type: FieldInputType.TEXT,
            label: 'Note Tracking # to Investor',
            variant: 'outlined',
            widthInColumns: 1,
          },
        },
        {
          fieldId: 'noteShippedToInvestorDate',
          type: FieldType.DATE,
          nameInModel: 'noteShippedToInvestorDate',
          inputProps: {
            label: 'Note Shipped to Investor Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 1,
          },
        },
        {
          fieldId: 'noteReceivedToInvestorDate',
          type: FieldType.DATE,
          nameInModel: 'noteReceivedToInvestorDate',
          inputProps: {
            label: 'Note Received to Investor Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 1,
          },
        },
        getShortTextField({
          fieldId: 'noteTrackingNumberFromOriginalInvestor',
          label: 'Note Tracking # from Original Investor',
          dataTestId:
            EditDrawerTestIds.SHIPPING_FORM_NOTE_TRACKING_NUMBER_FROM_ORIGINAL_INVESTOR_INPUT,
        }),
      ],
    },
    {
      type: FieldType.GROUP,
      key: 'shipping-original-mortgage-to-investor',
      title: 'Original Mortgage to Investor',
      customStyles: shippingGroupStyles,
      fields: [
        getShortTextField({
          fieldId: 'originalMortgageTrackingNumberToInvestor',
          label: 'Original Mortgage Tracking # to Investor',
          dataTestId: EditDrawerTestIds.SHIPPING_FORM_ORIGINAL_MORTGAGE_TRACKING_NUMBER_INPUT,
        }),
      ],
    },
  ],
};

export const PAYMENT_FORM_CONFIG = {
  groups: [
    {
      type: FieldType.GROUP,
      key: 'payment-info',
      title: 'Payment Information',
      fields: [
        {
          fieldId: 'autoDebitPayment',
          type: FieldType.RADIO,
          nameInModel: 'autoDebitPayment',
          inputProps: {
            label: 'Auto Debit Payment',
            variant: 'outlined',
            options: baseStatusOptions,
            widthInColumns: 2,
          },
        },
      ],
    },
  ],
};

const FINAL_DOCUMENTS_FORM_CONFIG = {
  groups: [
    {
      type: FieldType.GROUP,
      key: 'final-documents-dates',
      title: 'Final Documents',
      customStyles: fixedTrackGroupStyles,
      fields: [
        {
          fieldId: 'closedPackageReceivedDate',
          type: FieldType.DATE,
          nameInModel: 'closedPackageReceivedDate',
          inputProps: {
            label: 'Closed Package Received Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'investorLockDate',
          type: FieldType.DATE,
          nameInModel: 'investorLockDate',
          inputProps: {
            label: 'Investor Lock Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'investorLockExpiration',
          type: FieldType.DATE,
          nameInModel: 'investorLockExpiration',
          inputProps: {
            label: 'Investor Lock Expiration',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        getCheckboxField({
          fieldId: 'noteMissingFromClosedPackage',
          label: 'Note Missing from Closed Package',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_NOTE_MISSING_CHECKBOX,
        }),
        getCheckboxField({
          fieldId: 'postClosingReviewCompleted',
          label: 'Post-Closing Review Completed',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_REVIEW_COMPLETED_CHECKBOX,
        }),
        getLongTextField({
          fieldId: 'postClosingReviewDefects',
          label: 'Post-Closing Review Defects',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_REVIEW_DEFECTS_INPUT,
        }),
        getDateField({
          fieldId: 'preppedForTransmissionDate',
          label: 'Prepped for Transmission Date',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_PREPPED_FOR_TRANSMISSION_DATE,
        }),
        getDateField({
          fieldId: 'loanTransmittedDate',
          label: 'Loan Transmitted Date',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_LOAN_TRANSMITTED_DATE,
        }),
        {
          fieldId: 'transmissionConfirmationDate',
          type: FieldType.DATE,
          nameInModel: 'transmissionConfirmationDate',
          inputProps: {
            label: 'Transmission Confirmation Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'correctionInProcessDate',
          type: FieldType.DATE,
          nameInModel: 'correctionInProcessDate',
          inputProps: {
            label: 'Correction In Process Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        getLongTextField({
          fieldId: 'transmissionComments',
          label: 'Transmission Comments',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_TRANSMISSION_COMMENTS_INPUT,
        }),
        {
          fieldId: 'unrecordedMortgageReceivedDate',
          type: FieldType.DATE,
          nameInModel: 'unrecordedMortgageReceivedDate',
          inputProps: {
            label: 'Unrecorded Mortgage Received Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'copyOfRecordedMortgageReceivedDate',
          type: FieldType.DATE,
          nameInModel: 'copyOfRecordedMortgageReceivedDate',
          inputProps: {
            label: 'Copy of Recorded Mortgage Received Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'recordedMortgageUploadedDate',
          type: FieldType.DATE,
          nameInModel: 'recordedMortgageUploadedDate',
          inputProps: {
            label: 'Recorded Mortgage Uploaded Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        getDateField({
          fieldId: 'recordedMortgageRequestedDate',
          label: 'Recorded Mortgage Requested Date',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_RECORDED_MORTGAGE_REQUESTED_DATE,
        }),
        getDateField({
          fieldId: 'originalMortgageDeliveredDate',
          label: 'Original Mortgage Delivered Date',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_ORIGINAL_MORTGAGE_DELIVERED_DATE,
          isDoubleWidth: true,
        }),
        getDateField({
          fieldId: 'recordedMortgageDeliveredToInvestorDate',
          label: 'Recorded Mortgage Delivered to Investor Date',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_RECORDED_MORTGAGE_DELIVERED_DATE,
          isDoubleWidth: true,
        }),
        getLongTextField({
          fieldId: 'recordedMortgageNotes',
          label: 'Recorded Mortgage Notes',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_RECORDED_MORTGAGE_NOTES_INPUT,
        }),
        {
          fieldId: 'titlePolicyUploadedDate',
          type: FieldType.DATE,
          nameInModel: 'titlePolicyUploadedDate',
          inputProps: {
            label: 'Title Policy Uploaded Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        getCheckboxField({
          fieldId: 'mortgagePaidOffTitlePolicyNotRequired',
          label: 'Mortgage Paid Off - Title Policy Not Required',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_TITLE_POLICY_NOT_REQUIRED_CHECKBOX,
          isDoubleWidth: true,
        }),
        getDateField({
          fieldId: 'titlePolicyRequestedDate',
          label: 'Title Policy Requested Date',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_TITLE_POLICY_REQUESTED_DATE,
        }),
        getDateField({
          fieldId: 'titlePolicyReceivedDate',
          label: 'Title Policy Received Date',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_TITLE_POLICY_RECEIVED_DATE,
        }),
        getDateField({
          fieldId: 'titlePolicyCorrectionInProcessDate',
          label: 'Title Policy Correction In Process Date',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_TITLE_POLICY_CORRECTION_DATE,
        }),
        getDateField({
          fieldId: 'titlePolicyDeliveredToInvestorDate',
          label: 'Title Policy Delivered to Investor Date',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_TITLE_POLICY_DELIVERED_DATE,
          isDoubleWidth: true,
        }),
        getLongTextField({
          fieldId: 'titlePolicyNotes',
          label: 'Title Policy Notes',
          dataTestId: EditDrawerTestIds.FINAL_DOCUMENTS_FORM_TITLE_POLICY_NOTES_INPUT,
        }),
        {
          fieldId: 'tpMtgInFileCabinetDate',
          type: FieldType.DATE,
          nameInModel: 'tpMtgInFileCabinetDate',
          inputProps: {
            label: 'TP Mtg In File Cabinet Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'noteReturnedDate',
          type: FieldType.DATE,
          nameInModel: 'noteReturnedDate',
          inputProps: {
            label: 'Note Returned Date',
            variant: 'outlined',
            format: COMMON_DATE_FORMAT,
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'noteReturnedReason',
          type: FieldType.INPUT,
          nameInModel: 'noteReturnedReason',
          inputProps: {
            type: FieldInputType.TEXT,
            label: 'Note Returned Reason',
            variant: 'outlined',
            widthInColumns: 2,
            wrapperStyles: doubleWidthFieldWrapperStyles,
          },
        },
        {
          fieldId: 'finalDocumentsComments',
          type: FieldType.INPUT,
          nameInModel: 'finalDocumentsComments',
          inputProps: {
            type: FieldInputType.TEXT,
            label: 'Final Documents Comments',
            variant: 'outlined',
            multiline: true,
            rows: 3,
            widthInColumns: 4,
            wrapperStyles: fullRowFieldWrapperStyles,
          },
        },
      ],
    },
  ],
};

const SHIPPER_MILESTONE_FORM_CONFIG = {
  groups: [
    {
      type: FieldType.GROUP,
      key: 'shipper-milestone',
      title: 'Shipper Milestone',
      fields: [
        {
          fieldId: 'closingPackageUploaded',
          type: FieldType.CHECKBOX,
          nameInModel: 'closingPackageUploaded',
          inputProps: {
            label: 'Closing Package Uploaded',
            widthInColumns: 2,
          },
        },
        {
          fieldId: 'creditPackageUploaded',
          type: FieldType.CHECKBOX,
          nameInModel: 'creditPackageUploaded',
          inputProps: {
            label: 'Credit Package Uploaded',
            widthInColumns: 2,
          },
        },
      ],
    },
  ],
};

const getMersFormConfig = ({ postClosingSelectOptions = {} } = {}) => ({
  groups: [
    {
      type: FieldType.GROUP,
      key: 'mers-information',
      title: 'MERS',
      customStyles: fixedTrackGroupStyles,
      fields: [
        getShortTextField({
          fieldId: 'mersTransferBatchNumber',
          label: 'MERS Transfer Batch Number',
          dataTestId: EditDrawerTestIds.MERS_FORM_TRANSFER_BATCH_NUMBER_INPUT,
          isDoubleWidth: true,
        }),
        getDateField({
          fieldId: 'mersTransferDate',
          label: 'MERS Transfer Date',
          dataTestId: EditDrawerTestIds.MERS_FORM_TRANSFER_DATE,
        }),
        getDateField({
          fieldId: 'mersTransferAcceptedDate',
          label: 'MERS Transfer Accepted Date',
          dataTestId: EditDrawerTestIds.MERS_FORM_TRANSFER_ACCEPTED_DATE,
        }),
        getDateField({
          fieldId: 'minRegistrationReversedDate',
          label: 'MIN Registration Reversed Date',
          dataTestId: EditDrawerTestIds.MERS_FORM_REGISTRATION_REVERSED_DATE,
        }),
        getDateField({
          fieldId: 'minDeactivatedDate',
          label: 'MIN Deactivated Date',
          dataTestId: EditDrawerTestIds.MERS_FORM_DEACTIVATED_DATE,
        }),
        getSelectField({
          fieldId: 'minDeactivationType',
          label: 'MIN Deactivation Type',
          options: postClosingSelectOptions.minDeactivationType ?? [],
          dataTestId: EditDrawerTestIds.MERS_FORM_DEACTIVATION_TYPE_SELECT,
          isDoubleWidth: true,
        }),
        getLongTextField({
          fieldId: 'minRegistrationReversalReason',
          label: 'MIN Registration Reversal Reason',
          dataTestId: EditDrawerTestIds.MERS_FORM_REVERSAL_REASON_INPUT,
        }),
      ],
    },
  ],
});

const getFlippedUnsalableFormConfig = ({ postClosingSelectOptions = {} } = {}) => ({
  groups: [
    {
      type: FieldType.GROUP,
      key: 'flipped-unsalable',
      title: 'Flipped / Unsalable',
      customStyles: fixedTrackGroupStyles,
      fields: [
        getDateField({
          fieldId: 'flippedToNewInvestorDate',
          label: 'Flipped to New Investor Date',
          dataTestId: EditDrawerTestIds.FLIPPED_UNSALABLE_FORM_FLIPPED_DATE,
        }),
        getDateField({
          fieldId: 'markedUnsalableDate',
          label: 'Marked Unsalable Date',
          dataTestId: EditDrawerTestIds.FLIPPED_UNSALABLE_FORM_MARKED_UNSALABLE_DATE,
        }),
        getDateField({
          fieldId: 'noteReturnRequestedDate',
          label: 'Note Return Requested Date',
          dataTestId: EditDrawerTestIds.FLIPPED_UNSALABLE_FORM_NOTE_RETURN_REQUESTED_DATE,
        }),
        getSelectField({
          fieldId: 'unsalableResponsibleParty',
          label: 'Unsalable Responsible Party',
          options: postClosingSelectOptions.unsalableResponsibleParty ?? [],
          dataTestId: EditDrawerTestIds.FLIPPED_UNSALABLE_FORM_RESPONSIBLE_PARTY_SELECT,
        }),
        getSelectField({
          fieldId: 'unsalableReason',
          label: 'Unsalable Reason',
          options: postClosingSelectOptions.unsalableReason ?? [],
          dataTestId: EditDrawerTestIds.FLIPPED_UNSALABLE_FORM_REASON_SELECT,
        }),
        getLongTextField({
          fieldId: 'purchaseDenialReason',
          label: 'Purchase Denial Reason',
          dataTestId: EditDrawerTestIds.FLIPPED_UNSALABLE_FORM_PURCHASE_DENIAL_REASON_INPUT,
        }),
      ],
    },
  ],
});

export const SECTION_FORM_CONFIG_MAP = {
  [SECTION_HREF_MAP.INVESTOR]: getInvestorFormConfig,
  [SECTION_HREF_MAP.ACCOUNTING]: getAccountingFormConfig,
  [SECTION_HREF_MAP.SERVICER]: getServicerFormConfig,
  [SECTION_HREF_MAP.MERS]: getMersFormConfig,
  [SECTION_HREF_MAP.SHIPPING]: SHIPPING_FORM_CONFIG,
  [SECTION_HREF_MAP.PAYMENT]: PAYMENT_FORM_CONFIG,
  [SECTION_HREF_MAP.FINAL_DOCUMENTS]: FINAL_DOCUMENTS_FORM_CONFIG,
  [SECTION_HREF_MAP.SHIPPER_MILESTONE]: SHIPPER_MILESTONE_FORM_CONFIG,
  [SECTION_HREF_MAP.FLIPPED_UNSALABLE]: getFlippedUnsalableFormConfig,
};
